import { BookingWithDetail, EventWithDetail } from "@garther/interfaces";
import { Button, Spinner, Table } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { Download, UserX } from "lucide-react";
import { useState } from "react";

import { AttendeeAnswersDrawer } from "./attendee-answers-drawer";

interface EventAttendeeProps {
  event: EventWithDetail;
  bookings: BookingWithDetail[];
  isLoading: boolean;
}

export function EventAttendee({ event, bookings, isLoading }: EventAttendeeProps) {
  const { t, formatDate } = useI18n();

  const [selectedSessionId, setSelectedSessionId] = useState<string>("all");
  const [selectedBooking, setSelectedBooking] = useState<BookingWithDetail | null>(null);

  const filteredBookings =
    selectedSessionId === "all"
      ? bookings
      : bookings.filter((b) => b.ticketTier.eventSessionId === selectedSessionId);

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-responsive-xl font-light text-text-primary uppercase font-literata mb-1">
              {t("eventDetail.attendees.title")}
            </p>
            <p className="text-responsive-base font-normal text-text-secondary">
              {t("eventDetail.attendees.subtitle")}
            </p>
          </div>

          <Button variant="outline" className="hidden" onClick={() => {}}>
            <Download className="h-4 w-4 mr-2" />
            {t("eventDetail.attendees.exportCsv")}
          </Button>
        </div>

        {/* Session filter */}
        {event.sessions.length > 0 && (
          <div className="flex items-center gap-3">
            <label
              htmlFor="session-filter"
              className="text-responsive-sm font-medium text-text-secondary shrink-0"
            >
              {t("eventDetail.attendees.sessionFilter.label")}
            </label>
            <select
              id="session-filter"
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="text-responsive-sm rounded-md border border-border bg-background-primary text-text-primary px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="all">{t("eventDetail.attendees.sessionFilter.all")}</option>
              {event.sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {formatDate(session.startAt, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </option>
              ))}
            </select>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="w-8 h-8" />
          </div>
        ) : filteredBookings.length > 0 ? (
          <div className="bg-background-secondary rounded-md overflow-hidden border border-border">
            <Table
              data={filteredBookings}
              columns={[
                {
                  key: "name",
                  title: t("common.name"),
                  dataIndex: "user.name"
                },
                {
                  key: "email",
                  title: t("common.email"),
                  dataIndex: "user.email"
                },
                {
                  key: "session",
                  title: "Session",
                  render: (_, record: BookingWithDetail) =>
                    formatDate(record.ticketTier.eventSession.startAt, {
                      day: "numeric",
                      month: "short",
                      year: "numeric"
                    })
                },
                {
                  key: "ticket",
                  title: "Ticket Tier",
                  dataIndex: "ticketTier.title"
                },
                {
                  key: "quantity",
                  title: "Qty",
                  dataIndex: "quantity"
                },
                {
                  key: "answers",
                  title: t("eventDetail.attendees.actions.title"),
                  render: (_, record: BookingWithDetail) => (
                    <Button variant="ghost" padding="px-0" onClick={() => setSelectedBooking(record)}>
                      {t("eventDetail.attendees.actions.viewAnswers")}
                    </Button>
                  )
                }
              ]}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="max-w-md text-center space-y-6">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/10 rounded-full blur-2xl" />
                  <div className="relative bg-primary/5 p-6 rounded-full">
                    <UserX className="w-14 h-14 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-responsive-2xl font-medium text-text-primary">
                  {t("eventDetail.attendees.empty.title")}
                </h3>
                <p className="text-responsive-md text-text-secondary">
                  {t("eventDetail.attendees.empty.description")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <AttendeeAnswersDrawer booking={selectedBooking} onClose={() => setSelectedBooking(null)} />
    </>
  );
}
